import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, RefObject } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useCardBrand } from './use-card-brand';
import { useMerchantInfo } from './use-merchant-info';
import { useFormFeedback } from '@tectonics/components/form/hooks/use-form-feedback';
import { useClearSecurityCodeAfterFailedAttempt } from './use-clear-security-code-after-failed-attempt';
import { useUserInputValueWithFormatting } from '@tectonics/prs/user-input-values';
import { useSecurityCodeErrorMessage } from './use-form-security-code-error-message';
import type { CardBrand } from '@tectonics/prs/card-brand';
import type { LegacyCopyNamespace } from '../legacy-copy';

interface FormSecurityCodeField {
  readonly inputId: string;
  readonly cardDigits: number;
  readonly cardBrand?: CardBrand | null;
  readonly isDemoMerchant: boolean;
  readonly showPeruTooltip: boolean;
  readonly hasError: boolean;
  readonly errorMessage: string | null;
  readonly submitting: boolean;
  readonly value: string;
  readonly handleChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  readonly inputRef: RefObject<HTMLInputElement>;
}

function onlyDigits(value: string): string {
  return value.replace(/\D+/gu, '');
}

function useFormSecurityCodeField(legacyNamespace: LegacyCopyNamespace): FormSecurityCodeField {
  const inputId = useUniqueId();
  const { cardDigits, cardBrand } = useCardBrand();
  const { isDemoMerchant, isPeru } = useMerchantInfo();
  const { submitting, formHasBeenSent, validationError } = useFormFeedback('cardSecurityCode');
  const [editedAfterSubmit, setEditedAfterSubmit] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const formatSecurityCode = useCallback(
    (value: string): string => onlyDigits(value).slice(0, cardDigits),
    [cardDigits]
  );

  const [value, setValue] = useUserInputValueWithFormatting(
    'cardSecurityCode',
    formatSecurityCode,
    formatSecurityCode
  );

  useClearSecurityCodeAfterFailedAttempt(isDemoMerchant, setValue);

  useEffect(() => {
    const form = inputRef.current?.form;

    if (form == null) return undefined;

    const restoreValidationDisplay = (): void => setEditedAfterSubmit(false);
    form.addEventListener('submit', restoreValidationDisplay);

    return () => form.removeEventListener('submit', restoreValidationDisplay);
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setEditedAfterSubmit(true);
      setValue(formatSecurityCode(event.target.value));
    },
    [setValue, formatSecurityCode]
  );

  const hasError = Boolean(formHasBeenSent && validationError && !editedAfterSubmit);
  const errorMessage = useSecurityCodeErrorMessage(value, legacyNamespace);

  return {
    inputId,
    cardDigits,
    cardBrand,
    isDemoMerchant,
    showPeruTooltip: isPeru && !isDemoMerchant && legacyNamespace === 'volcanes',
    hasError,
    errorMessage,
    submitting,
    value,
    handleChange,
    inputRef,
  };
}

export { useFormSecurityCodeField };
